import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { MaskedLine } from "./Reveal";
import { scrollTo } from "./Nav";

const HERO_IMG =
  "https://images.unsplash.com/photo-1741850419928-aa65b3d5e3a6?fm=jpg&q=80&w=2400&auto=format&fit=crop";

export const Hero = () => {
  const { t, i18n } = useTranslation();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden grain" data-testid="hero-section">
      <motion.div className="absolute inset-0 will-change-transform" style={{ y: imgY, scale: 1.15 }}>
        <motion.img
          src={HERO_IMG}
          alt={t("hero.imgAlt")}
          className="h-full w-full object-cover"
          initial={{ scale: 1.12, opacity: 0.6 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1] }}
          data-testid="hero-image"
        />
      </motion.div>
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0D0F12] via-transparent to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 flex h-full flex-col justify-end px-6 pb-14 lg:px-12 lg:pb-16 max-w-7xl mx-auto">
        <MaskedLine delay={0.4}>
          <span className="font-code text-[11px] uppercase tracking-[0.35em] text-[#D6705A]" data-testid="hero-overline">
            {t("hero.overline")}
          </span>
        </MaskedLine>

        <h1 className="mt-6 font-display font-light leading-[0.95] tracking-tight text-[#F9F8F6] text-4xl sm:text-5xl lg:text-[5.75rem]" data-testid="hero-heading">
          <MaskedLine delay={0.55} key={`a-${i18n.language}`}>{t("hero.h1a")}</MaskedLine>
          <MaskedLine delay={0.7} key={`b-${i18n.language}`}>
            {t("hero.h1pre")} <em className="italic text-[#D6705A]">{t("hero.h1em")}</em>
          </MaskedLine>
          <MaskedLine delay={0.85} key={`c-${i18n.language}`}>{t("hero.h1post")}</MaskedLine>
        </h1>

        <MaskedLine delay={1.0} key={`kw-${i18n.language}`}>
          <h2 className="mt-4 font-code text-[11px] uppercase tracking-[0.35em] text-[#A0A0A0]" data-testid="hero-keywords">
            {t("hero.h1Keywords")}
          </h2>
        </MaskedLine>

        <div className="mt-7 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <MaskedLine delay={1.05}>
            <p className="max-w-md text-sm leading-relaxed text-[#C9C7C3] md:text-base" data-testid="hero-subtitle">
              {t("hero.subtitle")}
            </p>
          </MaskedLine>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollTo("#prenota")}
              className="rounded-full bg-[#D6705A] px-8 py-4 font-code text-[11px] uppercase tracking-[0.25em] text-[#0D0F12] transition-[background-color,transform] duration-300 hover:bg-[#F9F8F6] hover:-translate-y-1"
              data-testid="hero-cta-booking"
            >
              {t("hero.ctaBooking")}
            </button>
            <button
              onClick={() => scrollTo("#portfolio")}
              className="rounded-full border border-white/25 px-8 py-4 font-code text-[11px] uppercase tracking-[0.25em] text-[#F9F8F6] transition-[border-color,background-color,transform] duration-300 hover:border-[#F9F8F6] hover:bg-white/5 hover:-translate-y-1"
              data-testid="hero-cta-portfolio"
            >
              {t("hero.ctaPortfolio")}
            </button>
          </motion.div>
        </div>
      </motion.div>

      <motion.button
        onClick={() => scrollTo("#servizi")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 right-6 lg:right-12 z-10 hidden md:flex h-12 w-12 items-center justify-center rounded-full border border-white/20 text-[#F9F8F6] transition-colors duration-300 hover:border-[#D6705A] hover:text-[#D6705A]"
        data-testid="hero-scroll-indicator"
        aria-label={t("hero.scrollAria")}
      >
        <motion.span animate={{ y: [0, 5, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ArrowDown className="h-4 w-4" />
        </motion.span>
      </motion.button>
    </section>
  );
};
